// Гант отчёта о пропусках: строка — судно с именем, в строке уровень на источник
// (бусинка — точка, полоса его цвета — пропуск источника), фон строки — общий
// пропуск, когда молчали все источники. Под судном соседи с сигналом (янтарные),
// свёрнуты аккордеоном: клик по имени разворачивает их. Клик по общему пропуску
// ведёт к нему карту. Колесо масштабирует вокруг курсора, перетаскивание
// сдвигает, двойной щелчок возвращает окно отчёта. Геометрия — `ganttLayout.ts`.

import { useEffect, useMemo, useRef, useState } from 'react'
import type { AisGap, AisGapResult } from '../api'
import { formatDayTime, formatNumber } from '../i18n/format'
import { useLang, useT } from '../i18n'
import {
  GAP_COLOR, NEIGHBOR_COLOR, PORT_GAP_COLOR, SOURCE_COLORS, clampView, ganttRows, neighborLabel, panBy, toggleSource,
  scaleX, sourceLevels, taperPath, tickLabel, tickPath, timeTicks, uncovered, zoomAt, type SourceLane,
} from './ganttLayout'

const LABEL_W = 250
const LANE_H = 11
const NEIGHBOR_H = 17
const AXIS_H = 24
const COUNTS_W = 92
const PAD_R = 8
/** Сдвиг мыши больше этого — перетаскивание, а не клик. */
const DRAG_PX = 3

const iso = (t: number) => new Date(t * 1000).toISOString()
const NO_SOURCES: SourceLane[] = []

function useWidth(ref: React.RefObject<HTMLDivElement | null>): number {
  const [w, setW] = useState(900)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(([e]) => setW(Math.max(560, Math.floor(e.contentRect.width))))
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref])
  return w
}

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s)

interface Props {
  /** Общий пропуск (или единственный источник старого отчёта). */
  result: AisGapResult
  /** Уровни источников; пусто — один уровень из `result`. */
  sources?: SourceLane[]
  onGap: (no: number) => void
}

export function GapsGantt({ result, sources: allSources = NO_SOURCES, onGap }: Props) {
  // Фильтр источников: скрытый источник снимает свой уровень и число точек.
  const [hidden, setHidden] = useState<ReadonlySet<number>>(() => new Set())
  const sources = useMemo(() => allSources.filter(s => !hidden.has(s.no)), [allSources, hidden])
  const t = useT()
  const { lang } = useLang()
  const box = useRef<HTMLDivElement | null>(null)
  const svg = useRef<SVGSVGElement | null>(null)
  const width = useWidth(box)
  const full = result.window
  const [view, setView] = useState<[number, number]>(full)
  useEffect(() => setView(full), [full[0], full[1]]) // eslint-disable-line react-hooks/exhaustive-deps
  const [open, setOpen] = useState<ReadonlySet<number>>(() => new Set())
  const rows = useMemo(() => ganttRows(result, open, sources), [result, open, sources])
  const withNeighbors = useMemo(
    () => ganttRows(result, new Set(), sources).filter(r => r.neighbors).map(r => r.mmsi), [result, sources])
  const allOpen = withNeighbors.length > 0 && withNeighbors.every(m => open.has(m))
  const toggle = (mmsi: number) => setOpen(o => {
    const n = new Set(o)
    if (n.has(mmsi)) n.delete(mmsi); else n.add(mmsi)
    return n
  })
  const plotR = width - COUNTS_W - PAD_R
  const x = scaleX(view, LABEL_W, plotR)
  const ticks = timeTicks(view, Math.max(3, Math.floor((plotR - LABEL_W) / 90)))
  const daily = ticks.length > 1 && ticks[1] - ticks[0] >= 86400
  const lanes = Math.max(1, sources.length)
  const vesselH = Math.max(24, lanes * LANE_H + 8)
  const zoomed = view[0] !== full[0] || view[1] !== full[1]

  // Колесо — масштаб вокруг курсора. Слушатель не пассивный: иначе страница
  // прокручивалась бы вместе с масштабом.
  const viewRef = useRef(view)
  viewRef.current = view
  useEffect(() => {
    const el = svg.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      const px = e.clientX - el.getBoundingClientRect().left
      if (px < LABEL_W || px > plotR) return
      e.preventDefault()
      const v = viewRef.current
      const at = v[0] + ((px - LABEL_W) / Math.max(1, plotR - LABEL_W)) * (v[1] - v[0])
      setView(zoomAt(v, full, at, e.deltaY > 0 ? 1.25 : 0.8))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [full, plotR])

  const drag = useRef<{ x: number; view: [number, number]; moved: boolean } | null>(null)
  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    const px = e.clientX - e.currentTarget.getBoundingClientRect().left
    if (px < LABEL_W || e.button !== 0) return
    drag.current = { x: e.clientX, view, moved: false }
  }
  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const d = drag.current
    if (!d) return
    const dx = e.clientX - d.x
    if (!d.moved && Math.abs(dx) < DRAG_PX) return
    if (!d.moved) e.currentTarget.setPointerCapture(e.pointerId)
    d.moved = true
    const perPx = (d.view[1] - d.view[0]) / Math.max(1, plotR - LABEL_W)
    setView(panBy(d.view, full, -dx * perPx))
  }
  const endDrag = () => { setTimeout(() => { drag.current = null }, 0) }
  const clickGap = (no: number) => { if (!drag.current?.moved) onGap(no) }

  let y = AXIS_H
  const placed = rows.map(r => {
    const h = r.kind === 'vessel' ? vesselH : NEIGHBOR_H
    const at = y
    y += h
    return { r, at, h }
  })
  const height = y + 6
  const gapTip = (g: AisGap) => t('reports.gantt.gapTip', {
    no: g.no, from: formatDayTime(lang, iso(g.start)), to: formatDayTime(lang, iso(g.end)),
    hours: formatNumber(lang, g.hours, 1),
  })
  const inView = (a: number, b: number) => b > view[0] && a < view[1]

  return (
    <div ref={box} className="w-full overflow-x-auto">
      <div className="mb-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">
        {withNeighbors.length > 0 && (
          <button type="button" onClick={() => setOpen(allOpen ? new Set() : new Set(withNeighbors))}
            className="text-sonar hover:underline">
            {t(allOpen ? 'reports.gantt.collapseAll' : 'reports.gantt.expandAll')}
          </button>
        )}
        <span className="text-mist">{t('reports.gantt.zoomHint')}</span>
        {zoomed && (
          <button type="button" onClick={() => setView(full)} className="text-sonar hover:underline">
            {t('reports.gantt.reset')}
          </button>
        )}
      </div>
      <svg ref={svg} width={width} height={height} role="img" aria-label={t('reports.gantt.title')}
        className="block touch-none select-none text-[11px]"
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endDrag}
        onPointerCancel={endDrag} onDoubleClick={() => setView(clampView(full, full))}>
        <defs>
          <pattern id="gantt-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="currentColor" strokeWidth="2" className="text-hull" />
          </pattern>
          <clipPath id="gantt-plot"><rect x={LABEL_W} y={0} width={Math.max(0, plotR - LABEL_W)} height={height} /></clipPath>
        </defs>
        {ticks.map(tk => (
          <g key={tk}>
            <line x1={x(tk)} x2={x(tk)} y1={AXIS_H - 4} y2={height} stroke="currentColor"
              className="text-hull" strokeWidth={1} />
            {x(tk) < plotR - 70 && (
              <text x={x(tk) + 3} y={AXIS_H - 8} className="fill-mist tnum">{tickLabel(tk, daily)}</text>
            )}
          </g>
        ))}
        {placed.map(({ r, at, h }) => {
          const vessel = r.kind === 'vessel'
          const who = r.name ? `${r.name} (${r.mmsi})` : String(r.mmsi)
          const km = t('reports.gantt.km', { km: formatNumber(lang, r.km ?? 0, 1) })
          const label = vessel ? who : neighborLabel(r.name, r.mmsi, km, 36)
          const badStatus = vessel && r.status && r.status !== 'ok'
          const levels = vessel ? sourceLevels(r.lanes!.length, at + 2, h - 4) : []
          return (
            <g key={r.key}>
              {vessel && <line x1={0} x2={width} y1={at} y2={at} stroke="currentColor" className="text-hull/60" />}
              {vessel && r.neighbors ? (
                <g role="button" tabIndex={0} aria-expanded={open.has(r.mmsi)} className="cursor-pointer"
                  onClick={() => toggle(r.mmsi)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(r.mmsi) } }}>
                  <title>{t('reports.gantt.toggle', { name: label, count: r.neighbors })}</title>
                  <rect x={0} y={at} width={LABEL_W - 10} height={h} fill="transparent" />
                  <text x={6} y={at + h / 2 + 4} className="fill-mist">{open.has(r.mmsi) ? '▾' : '▸'}</text>
                  <text x={18} y={at + h / 2 + 4} className="fill-foam font-medium">{clip(label, 28)}</text>
                  <text x={LABEL_W - 12} y={at + h / 2 + 4} textAnchor="end"
                    className={sources.length ? 'tnum fill-mist' : 'tnum'}
                    fill={sources.length ? undefined : NEIGHBOR_COLOR}>{r.neighbors}</text>
                </g>
              ) : (
                <text x={vessel ? 18 : 26} y={at + h / 2 + 4}
                  className={vessel ? 'fill-foam font-medium' : 'fill-mist'}>
                  <title>{vessel ? label : `${who}, ${km}`}</title>
                  {vessel ? clip(label, 30) : label}
                </text>
              )}
              <g clipPath="url(#gantt-plot)">
                {/* Общий пропуск — фон строки целиком. */}
                {r.gaps.filter(g => inView(g.start, g.end)).map(g => {
                  const x0 = x(g.start)
                  const w = Math.max(2, x(g.end) - x0)
                  return (
                    <g key={g.no} className="cursor-pointer" onClick={() => clickGap(g.no)}>
                      <title>{gapTip(g)}</title>
                      <rect x={x0} y={at + 1} width={w} height={h - 2} rx={2}
                        fill={g.port ? PORT_GAP_COLOR : GAP_COLOR} opacity={0.22} />
                      {w > 16 && (
                        <text x={x0 + w / 2} y={at + 11} textAnchor="middle"
                          className="tnum font-semibold" fill={g.port ? PORT_GAP_COLOR : GAP_COLOR}>{g.no}</text>
                      )}
                    </g>
                  )
                })}
                {vessel && r.lanes!.map((l, i) => {
                  const yc = levels[i]
                  return (
                    <g key={l.no}>
                      {uncovered(full, l.window).filter(([a, b]) => inView(a, b)).map(([a, b]) => (
                        <rect key={a} x={x(a)} y={yc - 4} width={Math.max(1, x(b) - x(a))} height={8}
                          fill="url(#gantt-hatch)" />
                      ))}
                      {l.gaps.filter(([a, b]) => inView(a, b)).map(([a, b]) => (
                        <path key={a} d={taperPath(x(a), x(b), yc, 2.5)} fill={l.color} opacity={0.45} />
                      ))}
                      <path d={tickPath(l.stamps.filter(s => s >= view[0] && s <= view[1]), x, yc - 3.5, 7)}
                        stroke={l.color} strokeWidth={1.5} />
                    </g>
                  )
                })}
                {!vessel && !r.lanes && (
                  <path d={tickPath(r.stamps.filter(s => s >= view[0] && s <= view[1]), x, at + 3, h - 6)}
                    stroke={r.color} strokeWidth={1} opacity={0.85} />
                )}
                {/* Сосед по источникам: уровни как у судна, цвет источника. */}
                {!vessel && r.lanes && (() => {
                  const ys = sourceLevels(r.lanes.length, at + 1, h - 2)
                  const half = Math.max(2, (h - 2) / r.lanes.length / 2 - 0.5)
                  return r.lanes.map((l, i) => (
                    <path key={l.no} d={tickPath(l.stamps.filter(s => s >= view[0] && s <= view[1]), x, ys[i] - half, 2 * half)}
                      stroke={l.color} strokeWidth={1} opacity={0.9} />
                  ))
                })()}
              </g>
              {badStatus && (
                <text x={LABEL_W + 6} y={at + h / 2 + 4} className="fill-mist italic">
                  {t(`reports.vesselStatus.${r.status}`)}
                </text>
              )}
              {vessel && sources.length > 0 && (
                <text x={width - PAD_R} y={at + h / 2 + 4} textAnchor="end" className="tnum">
                  <title>{t('reports.gantt.countsTip')}</title>
                  {r.lanes!.map((l, i) => (
                    <tspan key={l.no} fill={l.color}>{i ? ' · ' : ''}{l.points}</tspan>
                  ))}
                </text>
              )}
            </g>
          )
        })}
      </svg>
      <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-[12px] text-mist">
        {allSources.length ? allSources.map(s => {
          const off = hidden.has(s.no)
          return (
            <button key={s.no} type="button" aria-pressed={!off} title={t('reports.gantt.sourceToggle')}
              onClick={() => setHidden(h => toggleSource(h, s.no, allSources.map(x => x.no)))}
              className={`flex items-center gap-1.5 rounded border px-1.5 py-0.5 ${off
                ? 'border-hull text-mist/50 line-through' : 'border-hull/80 text-foam hover:border-sonar'}`}>
              <svg aria-hidden width="14" height="10" opacity={off ? 0.3 : 1}><path d="M3 1v8M7 1v8M11 1v8" stroke={SOURCE_COLORS[s.no]} strokeWidth="1.5" /></svg>
              {t(`reports.source.${s.no}`)}
            </button>
          )
        }) : (
          <span className="flex items-center gap-1.5">
            <svg aria-hidden width="14" height="10"><path d="M3 1v8M7 1v8M11 1v8" stroke="#38bdf8" /></svg>
            {t('reports.gantt.points')}
          </span>
        )}
        {sources.length > 0 && (
          <span className="flex items-center gap-1.5">
            <svg aria-hidden width="18" height="10"><path d={taperPath(1, 17, 5, 2.5)} fill="#94a3b8" /></svg>
            {t('reports.gantt.sourceGap')}
          </span>
        )}
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-4 rounded-sm" style={{ background: GAP_COLOR, opacity: 0.5 }} />
          {t(sources.length ? 'reports.gantt.commonGap' : 'reports.gantt.gap')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-4 rounded-sm" style={{ background: PORT_GAP_COLOR, opacity: 0.5 }} />
          {t('reports.gantt.portGap')}
        </span>
        {sources.length > 0 && (
          <span className="flex items-center gap-1.5">
            <svg aria-hidden width="16" height="10" className="text-hull">
              <rect width="16" height="10" fill="url(#gantt-hatch)" />
            </svg>
            {t('reports.gantt.noCoverage')}
          </span>
        )}
        <span className="flex items-center gap-1.5">
          {sources.length ? (
            <span aria-hidden className="text-mist">↳</span>
          ) : (
            <svg aria-hidden width="14" height="10"><path d="M3 1v8M7 1v8M11 1v8" stroke={NEIGHBOR_COLOR} /></svg>
          )}
          {t(sources.length ? 'reports.gantt.neighborBySource' : 'reports.gantt.neighbor', { km: result.radius_km })}
        </span>
      </div>
    </div>
  )
}
